/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable css-modules/no-unused-class */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as yup from 'yup';
import { CircularProgress, useMediaQuery, useTheme } from '@material-ui/core';
import { FormApi } from 'final-form';
import { IApiResponse } from 'utils/models';
import Form from 'ui/Form/Form';
import { requiredOnly, validateAccount, validationCallbackOld } from 'utils/validation';
import { changeFieldValue, setFieldTouched } from 'utils/formMutators';
import Input, { Select, SelectItem, AccountInput } from 'ui/Input';
import { AxiosStatus } from 'api/axiosConfig';
import FieldsLeveler from 'ui/structure/FieldsLeveler/FieldsLeveler';
import SubmitLeveler from 'ui/structure/SubmitLeveler/SubmitLeveler';
import Button, { Sizes as ButtonSizes, Themes as ButtonThemes } from 'ui/Button/Button';
import { StatusModal, WarningModal } from 'components/modals';
import NavigateBackStep from 'ui/navigateBackStep/NavigateBackStep';
import api from 'api/api';
import Loader from 'ui/Loader/Loader';
import debounce from 'utils/debounce';
import auth from 'utils/auth';
import InputHint from 'components/modules/Beneficiaries/InputHint/InputHint';
import { IInstantBank, IBank, IBranch, IApiBranchReponse } from 'components/modules/Beneficiaries/Beneficiaries.model';
import { IBeneficiaryOtherBank } from './BenOtherBank.model';

import styles from './EditBenOtherBank.module.css';

import deleteIcon from 'assets/icons/delete.png';

let mainForm!: FormApi<IFormValues, Partial<IFormValues>>;

const schema = {
  otherBankTransfer: yup.object().shape({
    fullName: requiredOnly('Full name'),
    transferType: requiredOnly('Transfer Type'),
    bankCode: requiredOnly('Bank'),
    accountNumber: validateAccount(),
  }),
  otherBankTransferNonInstant: yup.object().shape({
    fullName: requiredOnly('Full name'),
    transferType: requiredOnly('Transfer Type'),
    bankCode: requiredOnly('Bank'),
    bankBranchCode: requiredOnly('Bank Branch'),
    accountNumber: validateAccount(),
  }),
};

interface IFormValues {
  fullName: string;
  transferType: string;
  bankCode: string;
  bankName: string;
  bankBranchCode?: string;
  bankBranch?: string;
  accountNumber: string;
}

interface IEditBenFab {
  data: IBeneficiaryOtherBank;
  onClose: () => void;
  instantBanks: IInstantBank[];
  nonInstantBanks: IBank[];
}

const EditBenOtherBank = ({ data, onClose, instantBanks, nonInstantBanks }: IEditBenFab) => {
  const [nameHint, setNameHint] = useState('');
  const [isLookingUp, setLookingUp] = useState(false);
  const [validatedAccountNo, setValidatedAccountNo] = useState(data.transferType === 'NON_INSTANT_INTERBANK');
  const [loadingData, setLoadingData] = useState(false);

  const [submitStatus, setSubmitStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const submitting = submitStatus === AxiosStatus.PENDING;

  const [loadingBranchList, setLoadingBranchList] = useState(false);
  const [branchList, setBranchList] = useState<IBranch[]>([]);

  const [actionType, setActionType] = useState<string>('');
  const [showWarning, setShowWarning] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const openWarning = (type: string) => () => {
    setActionType(type);
    setShowWarning(true);
  };
  const closeWarning = () => {
    setShowWarning(false);
  };

  const [showStatus, setShowStatus] = useState(false);
  const onCloseStatusModal = () => {
    setSuccessMessage('');
    setErrorMessage('');
    setShowStatus(false);
    setSubmitStatus(AxiosStatus.DEFAULT);
    onClose();
  };

  const downMdScreen = useMediaQuery(useTheme().breakpoints.down('sm'));

  const validate = useCallback((values: IFormValues) => {
    if (values.transferType === 'NON_INSTANT_INTERBANK') {
      return validationCallbackOld<unknown>(schema.otherBankTransferNonInstant)(values);
    }
    return validationCallbackOld<unknown>(schema.otherBankTransfer)(values);
  }, []);

  const intialValues: IFormValues = useMemo(() => {
    const iniitalData = {
      fullName: data.name,
      // eslint-disable-next-line max-len
      // ...(data.transferType === 'NON_INSTANT_INTERBANK' ? { instantType: 'NON-INSTANT' } : { instantType: 'INSTANT' }),
      transferType: data.transferType,
      bankCode: data.bankCode,
      bankName: data.bankName,
      bankBranchCode: data.branchCode,
      bankBranch: data.bankBranch,
      accountNumber: data.accountNumber,
    };

    return iniitalData;
  }, []);

  useEffect(() => {
    setLoadingData(true);
    api
      .get(`/transfer/bankBranch?bankSortCode=${data.bankCode}`, {})
      .then((response: IApiBranchReponse) => {
        setBranchList(response.banks);
      })
      .catch((error: IApiResponse) => {
        console.error(error);
      })
      .finally(() => {
        setLoadingData(false);
      });
  }, []);

  const searchBankBranch = (id: string) => {
    setLoadingBranchList(true);

    api
      .get(`/transfer/bankBranch?bankSortCode=${id}`, {})
      .then((response: IApiBranchReponse) => {
        setBranchList(response.banks);
      })
      .catch((error: IApiResponse) => {
        console.error(error);
      })
      .finally(() => {
        setLoadingBranchList(false);
      });
  };

  const handleChangeInstantType = ({ target }) => {
    mainForm.mutators.changeFieldValue(target?.name, target?.value); // Override default handleChange()
    mainForm.change('bankName', undefined);
    mainForm.change('bankCode', undefined);
    mainForm.change('bankBranch', undefined);
    mainForm.mutators.changeFieldValue('bankBranchCode', undefined);
    mainForm.change('accountNumber', '');
    setValidatedAccountNo(false);
    if (target?.value === 'NON_INSTANT_INTERBANK') {
      setNameHint('');
      setValidatedAccountNo(true);
    }
  };

  const handleChangeBank = (values: IFormValues) => ({ target }) => {
    const bankName = () => {
      if (values.transferType === 'INSTANT_INTERBANK') {
        return instantBanks.find((bank) => bank.code === target.value)?.name;
      }
      if (values.transferType === 'NON_INSTANT_INTERBANK') {
        return nonInstantBanks.find((bank: IBank) => bank.BankSortCode === target.value)?.BankName;
      }
      return undefined;
    };
    mainForm.mutators.changeFieldValue('bankBranchCode', undefined);
    mainForm.mutators.changeFieldValue(target?.name, target?.value); // Override default handleChange()
    mainForm.change('bankName', bankName());
    mainForm.change('bankBranch', undefined);
    if (values.transferType === 'NON_INSTANT_INTERBANK') {
      searchBankBranch(target.value);
    }
  };

  const lookupAccount = (bankCode: string, accountNumber: string) => {
    setLookingUp(true);
    api
      .post('transfer/otherbank/namelookup', {
        destinationAccount: accountNumber,
        sourceAccount: auth.data?.accountNumber,
        institutionId: bankCode,
      }, true, true)
      .then((response) => {
        if (response.name) {
          setNameHint(response.name);
          setValidatedAccountNo(true);
        } else {
          setNameHint('Account not found');
        }
      })
      .catch((error: IApiResponse) => {
        console.error(error);
        setNameHint(error.description);
      })
      .finally(() => setLookingUp(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounced = useCallback(debounce(lookupAccount, 300), []);

  const handleChangeAccountNumber = (values: IFormValues) => ({ target }) => {
    if (values.bankCode && values.transferType === 'INSTANT_INTERBANK') {
      setValidatedAccountNo(false);
      debounced(values.bankCode, target.value as string);
    }
  };

  const handleChangeBranch = ({ target }) => {
    mainForm.mutators.changeFieldValue(target?.name, target?.value); // Override default handleChange()
    const selectedBranch = branchList.find((branch) => branch.BranchSortCode === target?.value);
    mainForm.change('bankBranch', selectedBranch?.BranchName);
  };

  const onDeleteBeneficiary = (id: number) => {
    setSubmitStatus(AxiosStatus.PENDING);
    api
      .post('/beneficiary/transfer/delete', {
        beneficiaryId: id,
      })
      .then((response: IApiResponse) => {
        setSuccessMessage(response.description);
        setSubmitStatus(AxiosStatus.SUCCESS);
      })
      .catch((err: IApiResponse) => {
        setErrorMessage(err.description);
        setSubmitStatus(AxiosStatus.FAILURE);
      })
      .finally(() => {
        setShowStatus(true);
      });
  };

  const onSaveBeneficiary = (id: number, values: IFormValues) => {
    const body = {
      ...values,
      beneficiaryId: id,
    };

    api
      .put('/beneficiary/transfer/update', body)
      .then((response: IApiResponse) => {
        setSuccessMessage(response.description);
        setSubmitStatus(AxiosStatus.SUCCESS);
      })
      .catch((err: IApiResponse) => {
        setErrorMessage(err.description);
        setSubmitStatus(AxiosStatus.FAILURE);
      })
      .finally(() => {
        setShowStatus(true);
      });
  };

  const openConfirm = (values: IFormValues) => () => {
    setSubmitStatus(AxiosStatus.PENDING);

    return actionType === 'delete' ? onDeleteBeneficiary(data.id) : onSaveBeneficiary(data.id, values);
  };

  return (
    <>
      {loadingData ? (
        <Loader className="positionCenter" />
      ) : (
        <>
          <Form
            onSubmit={openWarning('save')}
            validate={validate}
            mutators={{ changeFieldValue, setFieldTouched }}
            initialValues={intialValues}
            render={({ handleSubmit, values, form, hasValidationErrors }) => {
              mainForm = form;
              return (
                <form onSubmit={handleSubmit}>
                  {!downMdScreen && <NavigateBackStep className="mt30" onClick={onClose}>Back</NavigateBackStep>}
                  <FieldsLeveler
                    label={(
                      <>
                        <div>Beneficiary details</div>
                        {!downMdScreen && (
                          <Button size={ButtonSizes.small} onClick={openWarning('delete')}>
                            <img src={deleteIcon} alt="trash-icon" />
                            &nbsp;Delete
                          </Button>
                        )}
                      </>
                    )}
                  >
                    {/* <pre>{JSON.stringify(branchList, null, 2)}</pre> */}
                    {/* <pre>{JSON.stringify(validatedAccountNo, null, 2)}</pre> */}
                    {/* <pre>{JSON.stringify(values, null, 2)}</pre> */}
                    {/* <pre>{JSON.stringify(errors, null, 2)}</pre> */}
                    <Input name="fullName" type="text" label="Full Name" disabled={submitting} />
                    <Select
                      name="transferType"
                      label="Transfer Type"
                      disabled
                      SelectProps={{
                        onChange: handleChangeInstantType,
                      }}
                    >
                      <SelectItem key="instant" value="INSTANT_INTERBANK">
                        Instant Transfer
                      </SelectItem>
                      <SelectItem key="non-Instant" value="NON_INSTANT_INTERBANK">
                        Non-Instant Transfer
                      </SelectItem>
                    </Select>
                    <Select
                      name="bankCode"
                      label="Bank"
                      disabled={submitting || loadingBranchList}
                      SelectProps={{
                        onChange: handleChangeBank(values),
                      }}
                    >
                      {values.transferType === 'INSTANT_INTERBANK'
                        && instantBanks.map(({ name, code }) => (
                          <SelectItem key={name} value={code}>
                            {name}
                          </SelectItem>
                        ))}
                      {values.transferType === 'NON_INSTANT_INTERBANK'
                        && nonInstantBanks.map(({ BankName, BankSortCode }) => (
                          <SelectItem key={BankName} value={BankSortCode}>
                            {BankName}
                          </SelectItem>
                        ))}
                    </Select>
                    {values.transferType === 'NON_INSTANT_INTERBANK' && (
                      <Select
                        name="bankBranchCode"
                        label="Bank Branch"
                        disabled={submitting || loadingBranchList}
                        SelectProps={{
                          onChange: handleChangeBranch,
                        }}
                      >
                        {branchList.map(({ BranchSortCode, BranchName }) => (
                          <SelectItem key={BranchSortCode} value={BranchSortCode}>
                            {BranchName}
                          </SelectItem>
                        ))}
                      </Select>
                    )}
                    <AccountInput
                      name="accountNumber"
                      label="Account Number"
                      disabled={submitting}
                      onInput={handleChangeAccountNumber(values)}
                    />
                    {(nameHint || isLookingUp) && (
                      <InputHint hint={nameHint} loading={isLookingUp} onClose={() => setNameHint('')} />
                    )}
                  </FieldsLeveler>
                  <SubmitLeveler>
                    {downMdScreen && (
                      <Button
                        type="button"
                        theme={ButtonThemes.secondary}
                        fullWidth
                        className="mb20"
                        onClick={openWarning('delete')}
                      >
                        Delete Beneficiary
                      </Button>
                    )}
                    <Button
                      type="submit"
                      fullWidth
                      disabled={hasValidationErrors || submitting || !form.getState().dirty || !validatedAccountNo}
                    >
                      {submitting ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                    </Button>
                  </SubmitLeveler>
                  <WarningModal
                    open={showWarning}
                    submitting={submitting}
                    submitName={actionType === 'delete' ? 'Delete' : 'Save'}
                    onSubmit={openConfirm(values)}
                    onClose={closeWarning}
                  >
                    {actionType === 'delete'
                      ? 'Are you sure you want to delete the Beneficiary?'
                      : 'Save changes to Beneficiary?'}
                  </WarningModal>
                  <StatusModal
                    open={showStatus}
                    status={submitStatus === AxiosStatus.SUCCESS ? 'success' : 'failure'}
                    onRetry={onCloseStatusModal}
                    onClose={onCloseStatusModal}
                    onRetryText="Continue"
                  >
                    {submitStatus === AxiosStatus.SUCCESS ? successMessage : errorMessage}
                  </StatusModal>
                </form>
              );
            }}
          />
        </>
      )}
    </>
  );
};

export default EditBenOtherBank;
